import { useCallback, useEffect } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { skipToken } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

export function useManageChats() {
  const projectId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const { pageId } = useParams();

  const [searchParams, setSearchParams] = useSearchParams();

  const chatId = searchParams.get("chatId");

  const { mutateAsync: createChat, isPending: createChatLoading } =
    trpc.chat.create.useMutation({
      onSuccess: (chat) => {
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("chatId", chat.id);
            return newParams;
          },
          { replace: true },
        );
        void trpcUtils.chat.listAll.invalidate({
          projectId,
          userId: user?.id ?? "",
          fileId: pageId ?? "",
        });
      },
    });

  const { data: chats, refetch: refetchChats } = trpc.chat.listAll.useQuery(
    projectId && pageId && user?.id
      ? { projectId, userId: user.id, fileId: pageId }
      : skipToken,
  );

  useEffect(() => {
    if (!projectId || !user?.id || !pageId) {
      return;
    }

    const queryParamChatId = searchParams.get("chatId");

    if (queryParamChatId) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", queryParamChatId);
          return newParams;
        },
        { replace: true },
      );
      return;
    }
  }, [projectId, searchParams, setSearchParams, user?.id, pageId]);

  const handleNewChat = useCallback(async () => {
    if (!projectId || !user?.id || !pageId) {
      return;
    }
    return await createChat({ projectId, userId: user?.id, fileId: pageId });
  }, [projectId, createChat, user?.id, pageId]);

  const handleChatSelect = useCallback(
    (selectedChatId: string) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", selectedChatId);
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    chatId,
    chats,
    createChatLoading,
    handleNewChat,
    handleChatSelect,
    refetchChats,
  };
}
